import { useEffect, useState } from 'react';
import { Check, Pencil, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { VoiceBodyRow } from '../payload';

/**
 * 30 reserved slots — 10 each for `custom`, `tts`, `sms`. Mirrors the
 * legacy `Webhooks/AddWebhooks` modal that ships these as the only allowed
 * custom-body parameter pool (sequential selection within a group, parameter
 * name renameable, slot key locks the wire value as `{custom_3}` etc).
 */
type Prefix = 'custom' | 'tts' | 'sms';
const PREFIXES: Prefix[] = ['custom', 'tts', 'sms'];
const SLOT_LABEL: Record<Prefix, string> = {
  custom: 'Custom',
  tts: 'TTS',
  sms: 'SMS',
};
const SLOTS_PER_GROUP = 10;

interface SlotState {
  checked: boolean;
  parameter: string;
  /** Wire token — always the slot key (e.g. `custom_3`). Not user-editable. */
  value: string;
  editing: boolean;
  tempParameter: string;
}

export type ModalSelections = Record<string, SlotState>;

export function buildModalSelections(): ModalSelections {
  const s: ModalSelections = {};
  for (const prefix of PREFIXES) {
    for (let i = 1; i <= SLOTS_PER_GROUP; i++) {
      const key = `${prefix}_${i}`;
      s[key] = {
        checked: false,
        parameter: key,
        value: key,
        editing: false,
        tempParameter: key,
      };
    }
  }
  return s;
}

/**
 * Restore the modal state from an existing bodyTable so re-opening the modal
 * shows the user which slots they had already picked.
 */
export function buildModalSelectionsFromTable(
  bodyTable: VoiceBodyRow[],
): ModalSelections {
  const selections = buildModalSelections();
  bodyTable
    .filter((r) => r.custom)
    .forEach((r) => {
      const match = r.value.match(/^\{(.+)\}$/);
      if (!match) return;
      const key = match[1];
      if (!key) return;
      const existing = selections[key];
      if (existing) {
        selections[key] = {
          ...existing,
          checked: true,
          parameter: r.parameter,
          tempParameter: r.parameter,
        };
      }
    });
  return selections;
}

const ALL_RESERVED_KEYS = PREFIXES.flatMap((p) =>
  Array.from({ length: SLOTS_PER_GROUP }, (_, i) => `${p}_${i + 1}`),
);

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Existing rows; used to hydrate state and avoid duplicate names on save. */
  bodyTable: VoiceBodyRow[];
  /** Called with the rows the user picked from the modal. Parent appends them. */
  onApply: (rowsToAppend: VoiceBodyRow[]) => void;
}

export function CustomBodyParamModal({ open, onOpenChange, bodyTable, onApply }: Props) {
  const [selections, setSelections] = useState<ModalSelections>(buildModalSelections);

  useEffect(() => {
    if (open) setSelections(buildModalSelectionsFromTable(bodyTable));
  }, [open, bodyTable]);

  const toggleCheckbox = (key: string) => {
    setSelections((prev) => {
      const sel = prev[key];
      if (!sel) return prev;
      const lastUnderscore = key.lastIndexOf('_');
      const prefix = key.slice(0, lastUnderscore);
      const index = Number(key.slice(lastUnderscore + 1));

      const next: ModalSelections = { ...prev };
      if (!sel.checked) {
        for (let i = 1; i < index; i++) {
          const prior = next[`${prefix}_${i}`];
          if (!prior?.checked) {
            toast.error(`Please select ${prefix}_${i} first.`);
            return prev;
          }
        }
        next[key] = { ...sel, checked: true };
      } else {
        for (let i = index; i <= SLOTS_PER_GROUP; i++) {
          const k = `${prefix}_${i}`;
          const slot = next[k];
          if (slot) next[k] = { ...slot, checked: false, editing: false };
        }
      }
      return next;
    });
  };

  const startEdit = (key: string) => {
    setSelections((prev) => {
      const slot = prev[key];
      if (!slot) return prev;
      return {
        ...prev,
        [key]: { ...slot, editing: true, tempParameter: slot.parameter },
      };
    });
  };

  const cancelEdit = (key: string) => {
    setSelections((prev) => {
      const slot = prev[key];
      if (!slot) return prev;
      return { ...prev, [key]: { ...slot, editing: false } };
    });
  };

  const changeTemp = (key: string, val: string) => {
    setSelections((prev) => {
      const slot = prev[key];
      if (!slot) return prev;
      return { ...prev, [key]: { ...slot, tempParameter: val } };
    });
  };

  const saveEdit = (key: string) => {
    setSelections((prev) => {
      const sel = prev[key];
      if (!sel) return prev;
      const trimmed = sel.tempParameter.trim();
      if (!trimmed) {
        toast.error('Parameter cannot be empty.');
        return prev;
      }
      const reserved = ALL_RESERVED_KEYS.filter((k) => k !== key);
      if (reserved.includes(trimmed)) {
        toast.error(`"${trimmed}" is a reserved name and cannot be used.`);
        return prev;
      }
      return {
        ...prev,
        [key]: { ...sel, parameter: trimmed, editing: false },
      };
    });
  };

  const applySelection = () => {
    const checked = Object.entries(selections).filter(([, v]) => v.checked);
    if (checked.length === 0) {
      toast.error('Please select at least one parameter.');
      return;
    }

    const existingNames = new Set(bodyTable.map((r) => r.parameter.toLowerCase()));
    const rowsToAppend: VoiceBodyRow[] = [];
    let nextId = bodyTable.length + 1;

    for (const [, sel] of checked) {
      const param = sel.parameter.trim();
      const value = sel.value.trim() || param;
      if (!param) continue;
      if (existingNames.has(param.toLowerCase())) continue;
      existingNames.add(param.toLowerCase());
      rowsToAppend.push({
        id: nextId++,
        parameter: param,
        value: `{${value}}`,
        selected: true,
        custom: true,
      });
    }

    onApply(rowsToAppend);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Select Body Parameters</DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-3">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {PREFIXES.map((prefix) => (
              <div key={prefix} className="space-y-3">
                <h4 className="text-sm font-semibold uppercase tracking-wide text-foreground">
                  {SLOT_LABEL[prefix]}
                </h4>
                <div className="space-y-2">
                  {Array.from({ length: SLOTS_PER_GROUP }, (_, i) => {
                    const key = `${prefix}_${i + 1}`;
                    const sel = selections[key];
                    if (!sel) return null;
                    return (
                      <div key={key} className="rounded-md border border-border p-2">
                        <label className="flex cursor-pointer items-center gap-2 text-sm">
                          <Checkbox
                            checked={sel.checked}
                            onCheckedChange={() => toggleCheckbox(key)}
                          />
                          <span className="font-mono">{key}</span>
                        </label>

                        {sel.checked &&
                          (sel.editing ? (
                            <div className="mt-2 flex items-center gap-1">
                              <Input
                                value={sel.tempParameter}
                                onChange={(e) => changeTemp(key, e.target.value)}
                                placeholder="Parameter"
                                className="h-7 flex-1 text-xs"
                              />
                              <span className="font-mono text-[10px] text-muted-foreground">
                                {`{${sel.value}}`}
                              </span>
                              <Button
                                type="button"
                                size="icon"
                                variant="default"
                                className="h-7 w-7"
                                onClick={() => saveEdit(key)}
                                aria-label="Save"
                              >
                                <Check className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                type="button"
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7"
                                onClick={() => cancelEdit(key)}
                                aria-label="Cancel"
                              >
                                <X className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          ) : (
                            <div className="mt-2 flex items-center gap-2">
                              <span className="text-xs">{sel.parameter}</span>
                              <span className="font-mono text-[10px] text-muted-foreground">
                                {`{${sel.value}}`}
                              </span>
                              <Button
                                type="button"
                                size="icon"
                                variant="ghost"
                                className="ml-auto h-7 w-7"
                                onClick={() => startEdit(key)}
                                aria-label="Edit"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={applySelection}>
            Add Parameters
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
